/**
 * Branded teal header — the LifeScribe AI Styles header's visual language
 * (dot texture, breadcrumb, Playfair title, stat chips, gold primary button)
 * with three deliberate differences:
 *
 *   • ONE "Add" button, labelled for the catalogue that is actually open. The
 *     original offered "Add Writing Style" / "Add Illustration Style" and then
 *     posted to the genre endpoint either way, so a style added from the Authors
 *     tab silently became a genre.
 *   • Export sits where the original puts it — paired with Refresh on the last
 *     row — but it is a client-side download of the OPEN catalogue. The
 *     original's Export called `/writing-styles/export`, which does not exist
 *     (404), and always exported "writing" whichever tab you were on.
 *   • The Quill backend health caption sits in the header, so the connection
 *     state is visible on the surface that writes to production.
 */

import React from 'react';
import { Box, Typography, Button, Chip, CircularProgress, Tooltip } from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import GetAppIcon from '@mui/icons-material/GetApp';
import { colors } from '../../../theme/designTokens';
import { HelpTip } from '../../help/HelpTip';

export interface AIStylesHeaderStats {
  activeCount: number;
  catalogueCount: number;
  inGridCount: number;
  totalCount: number;
  withheldCount: number;
}

export interface AIStylesHeaderProps {
  stats: AIStylesHeaderStats;
  /** Label for the single Add button, e.g. "Add writing author". */
  addLabel: string;
  onAdd: () => void;
  onRefresh: () => void;
  refreshing?: boolean;
  /** Import is only offered on the Writing Authors tab. */
  onImport?: () => void;
  /** Downloads the currently open catalogue as JSON — no server round-trip. */
  onExport: () => void;
  /** Label for the Export tooltip, e.g. "Writing Authors". */
  exportLabel: string;
  healthStatus: string;
}

const chipSx = {
  fontFamily: '"Montserrat", sans-serif',
  fontWeight: 600,
  fontSize: '0.72rem',
  bgcolor: 'rgba(255,255,255,0.1)',
  color: '#fff',
  '& .MuiChip-label': { px: 1.2 },
} as const;

const outlinedSx = {
  color: '#fff',
  borderColor: 'rgba(255,255,255,0.25)',
  fontFamily: '"Montserrat", sans-serif',
  fontWeight: 600,
  fontSize: '0.78rem',
  textTransform: 'none',
  borderRadius: '8px',
  px: 2,
  whiteSpace: 'nowrap',
  '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
} as const;

const AIStylesHeader: React.FC<AIStylesHeaderProps> = ({
  stats,
  addLabel,
  onAdd,
  onRefresh,
  refreshing = false,
  onImport,
  onExport,
  exportLabel,
  healthStatus,
}) => (
  <Box
    sx={{
      bgcolor: colors.deepTeal[500],
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '12px 12px 0 0',
      px: 3.5,
      pt: 3,
      pb: 2.5,
      display: 'flex',
      alignItems: 'stretch',
      flexWrap: { xs: 'wrap', md: 'nowrap' },
    }}
  >
    {/* Dot texture overlay */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: 0.06,
        backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
        backgroundSize: '18px 18px',
        pointerEvents: 'none',
      }}
    />

    <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: '"Montserrat", sans-serif',
          fontWeight: 500,
          fontSize: '0.72rem',
          color: 'rgba(255,255,255,0.4)',
          letterSpacing: '0.04em',
          mb: 1.5,
        }}
      >
        Admin Dashboard / Quill / AI Styles
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            bgcolor: 'rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PaletteIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Typography
          component="h1"
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 400,
            fontSize: '1.625rem',
            color: '#fff',
            letterSpacing: '-0.01em',
          }}
        >
          AI Styles
        </Typography>
        <HelpTip id="quill-ai-styles" />
      </Box>

      <Typography
        sx={{
          fontFamily: '"Montserrat", sans-serif',
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.55)',
          mb: 1,
        }}
      >
        Quill&rsquo;s own style catalogues &middot; the top four active styles fill the app&rsquo;s
        2&times;2 grid &middot; position <strong>#1</strong> wears &ldquo;Most Popular&rdquo;
      </Typography>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 2,
          fontFamily: '"Montserrat", sans-serif',
          color: healthStatus === 'up' ? '#4ade80' : 'rgba(255,255,255,0.5)',
        }}
      >
        ● Quill backend • {healthStatus === 'up' ? 'connected' : healthStatus}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={`${stats.activeCount} Active`}
          sx={chipSx}
          icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#4ade80', ml: 0.8 }} />}
        />
        <Chip size="small" label={`${stats.catalogueCount} Catalogues`} sx={chipSx} />
        <Chip
          size="small"
          label={`${stats.inGridCount} In Grid`}
          sx={chipSx}
          icon={
            <Box
              sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: colors.warmGold[500], ml: 0.8 }}
            />
          }
        />
        <Chip size="small" label={`${stats.totalCount} Total`} sx={chipSx} />
        {stats.withheldCount > 0 && (
          <Chip
            size="small"
            label={`${stats.withheldCount} Withheld`}
            sx={chipSx}
            icon={
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#f97316', ml: 0.8 }} />
            }
          />
        )}
      </Box>
    </Box>

    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1,
        position: 'relative',
        ml: { xs: 0, md: 3 },
        mt: { xs: 2, md: 0 },
      }}
    >
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={onAdd}
        sx={{
          bgcolor: colors.warmGold[500],
          color: '#fff',
          fontFamily: '"Montserrat", sans-serif',
          fontWeight: 600,
          fontSize: '0.78rem',
          textTransform: 'none',
          borderRadius: '8px',
          px: 2,
          whiteSpace: 'nowrap',
          '&:hover': { bgcolor: colors.warmGold[600] },
        }}
      >
        {addLabel}
      </Button>

      {onImport && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<CloudDownloadIcon />}
          onClick={onImport}
          sx={outlinedSx}
        >
          Import from LifeScribe
        </Button>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
          onClick={onRefresh}
          disabled={refreshing}
          sx={{ ...outlinedSx, flex: 1 }}
        >
          Refresh
        </Button>
        <Tooltip describeChild title={`Download the ${exportLabel} catalogue as JSON`}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={onExport}
            sx={{ ...outlinedSx, flex: 1 }}
          >
            Export
          </Button>
        </Tooltip>
      </Box>
    </Box>
  </Box>
);

export default AIStylesHeader;
